import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, ScanLine, Plus, ListChecks, BarChart3 } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BrandPicker, CaptureFlow } from "@/components/leaddesk/CaptureFlow";
import { LeadRegister } from "@/components/leaddesk/LeadRegister";
import { EventDashboard } from "@/components/leaddesk/EventDashboard";
import { WaStatus } from "@/components/leaddesk/WhatsAppPanel";

export default function LeadDesk() {
  const navigate = useNavigate();
  const [brand, setBrand] = useState(() => localStorage.getItem("ld_brand") || "univicoustic");
  const [eventName, setEventName] = useState(() => sessionStorage.getItem("scan_event_name") || "");
  const [tab, setTab] = useState("capture");
  const [done, setDone] = useState(null);
  const pick = (b) => { setBrand(b); localStorage.setItem("ld_brand", b); };

  return (
    <div data-testid="lead-desk-page">
      <PageHeader title="Lead Desk" subtitle="Exhibition workspace — scan the card, confirm interests, send the right catalogues while the conversation is fresh.">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-9">
            <TabsTrigger value="capture" className="text-xs gap-1.5" data-testid="ld-tab-capture"><ScanLine className="h-3.5 w-3.5" /> New lead</TabsTrigger>
            <TabsTrigger value="register" className="text-xs gap-1.5" data-testid="ld-tab-register"><ListChecks className="h-3.5 w-3.5" /> Lead register</TabsTrigger>
            <TabsTrigger value="events" className="text-xs gap-1.5" data-testid="ld-tab-events"><BarChart3 className="h-3.5 w-3.5" /> Events</TabsTrigger>
          </TabsList>
        </Tabs>
      </PageHeader>

      <div className="mb-5">
        <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Which brand are you welcoming visitors to?</p>
        <BrandPicker value={brand} onChange={pick} />
      </div>

      {tab === "capture" ? <CaptureFlow brand={brand} eventName={eventName} setEventName={setEventName} onCaptured={setDone} />
        : tab === "events" ? <EventDashboard brand={brand} /> : <LeadRegister brand={brand} />}

      <Dialog open={!!done} onOpenChange={(v) => { if (!v) setDone(null); }}>
        <DialogContent className="max-w-md" data-testid="ld-done-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Lead captured</DialogTitle></DialogHeader>
          {done && (
            <div className="text-sm text-slate-700 space-y-2">
              <p><b>{done.name}</b>{done.company ? ` · ${done.company}` : ""} saved as a Contact{done.contact_reused ? " (existing contact reused)" : ""} and an Exhibition lead.</p>
              <p className="text-xs text-slate-500">{done.interests?.length ? `${done.interests.length} catalogue${done.interests.length === 1 ? "" : "s"} matched.` : "No catalogues selected."}</p>
              {done.delivery?.status === "sent" && <p className="text-xs text-emerald-700" data-testid="ld-done-sent">Catalogue email sent to {done.email}{done.delivery.attached?.length ? ` with ${done.delivery.attached.length} PDF attached` : ""}.{done.delivery.pending?.length ? ` To follow: ${done.delivery.pending.join(", ")}.` : ""}</p>}
              {done.delivery?.status === "failed" && <p className="text-xs text-red-600" data-testid="ld-done-failed">Email failed: {done.delivery.error}. You can resend from the Lead register.</p>}
              {done.delivery?.status === "not_sent" && <p className="text-xs text-slate-500">No email sent.</p>}
              {done.whatsapp && done.whatsapp.status !== "not_sent" && <p className="text-xs" data-testid="ld-done-whatsapp"><WaStatus w={done.whatsapp} testid="ld-done-wa-status" />{done.whatsapp.status !== "sent" && done.whatsapp.error ? <span className="text-red-600"> — {done.whatsapp.error}</span> : null}</p>}
              <div className="flex gap-2 pt-2">
                <Button className="flex-1 gap-1.5 bg-slate-900 hover:bg-slate-800" onClick={() => setDone(null)} data-testid="ld-done-next"><Plus className="h-4 w-4" /> Next visitor</Button>
                <Button variant="outline" className="flex-1" onClick={() => { setDone(null); setTab("register"); }} data-testid="ld-done-register">View register</Button>
                <Button variant="ghost" onClick={() => navigate(`/leads/${done.lead_id}`)} data-testid="ld-done-open-lead">Open lead</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
